import { useId, useState } from 'react';
import { cn } from '@/lib/cn';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { TabPanel, Tabs } from '@/ui/Tabs';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useAreasDeGestion, useCursos } from '../api';
import { NIVELES } from '../piezas';
import {
  useAgregarRequisito, useBorrarCompetencia, useCargos, useCompetencia, useCompetencias,
  useCrearCompetencia, useDesvincularCurso, useEditarCompetencia, useQuitarRequisito, useVincularCurso,
  type CompetenciaDetalle, type CuerpoDeCompetencia,
} from './api';

/**
 * GestionDeCompetencias — crear competencias, decir qué cursos las desarrollan
 * y qué cargos o áreas las requieren.
 *
 * ## Tres modales que se sustituían, otra vez
 *
 * Como en las rutas: «Gestionar» abría un `Utils.modal`; «Editar datos» abría
 * OTRO en el mismo `#modal-overlay` y el panel desaparecía; «Eliminar» era un
 * `Utils.confirm` encima, que al cancelar dejaba a la persona sin panel. Y lo
 * que se cambiaba dentro no llegaba a la lista de detrás. Aquí es un `Drawer`
 * con tres pestañas y un `ConfirmDialog`, y la lista lee los mismos datos.
 *
 * ## Un requisito es de un cargo o de un área
 *
 * El vanilla ofrecía los dos campos a la vez —«Cargo» y «— o un área —»— y si se
 * rellenaban ambos guardaba un requisito con los dos, que el servidor trata como
 * «o» y la pantalla enseñaba solo por el cargo: el área quedaba invisible. Ahora
 * se elige uno.
 */
export function GestionDeCompetencias() {
  const lista = useCompetencias(true);
  const [creando, setCreando] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreando(true)}>
          <i className="bi bi-plus-circle-fill" aria-hidden="true" /> Nueva competencia
        </Button>
      </div>

      {lista.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : lista.isError ? (
        <EstadoError err={lista.error} que="las competencias" onReintentar={() => void lista.refetch()} />
      ) : !lista.data?.length ? (
        <EstadoVacio
          icono="bi-mortarboard"
          titulo="Sin competencias"
          descripcion="Crea la primera para construir la matriz: qué cursos la desarrollan y qué cargos la piden."
          accion={{ texto: 'Nueva competencia', onClick: () => setCreando(true) }}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {lista.data.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface p-3">
              <span
                className="flex size-10 shrink-0 items-center justify-center rounded-sm bg-sunken text-brand-ink"
                aria-hidden="true"
              >
                <i className="bi bi-mortarboard-fill" />
              </span>
              <div className="min-w-44 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-3 font-semibold text-ink">
                  {c.name}
                  {c.level_label && <Badge tone="info">{c.level_label}</Badge>}
                </p>
                <p className="flex flex-wrap gap-x-3 text-2 text-muted">
                  {c.area_name && (
                    <span><i className="bi bi-diagram-2" aria-hidden="true" /> {c.area_name}</span>
                  )}
                  <span>
                    <i className="bi bi-play-circle" aria-hidden="true" /> {c.course_count}{' '}
                    {c.course_count === 1 ? 'curso' : 'cursos'}
                  </span>
                  <span>
                    <i className="bi bi-briefcase" aria-hidden="true" /> {c.requirement_count}{' '}
                    {c.requirement_count === 1 ? 'requisito' : 'requisitos'}
                  </span>
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Gestionar la competencia ${c.name}`}
                onClick={() => setAbierta(c.id)}
              >
                <i className="bi bi-gear" aria-hidden="true" /> Gestionar
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Drawer
        abierto={creando}
        onCambio={(v) => { if (!v) setCreando(false); }}
        titulo="Nueva competencia"
        descripcion="Después dirás qué cursos la desarrollan y qué cargos la requieren."
        ancho="md"
      >
        {creando && (
          <FormularioDeCompetencia
            onHecho={(id) => {
              setCreando(false);
              if (id) setAbierta(id);
            }}
          />
        )}
      </Drawer>

      <GestionDeCompetencia id={abierta} onCerrar={() => setAbierta(null)} />
    </div>
  );
}

/* ── El panel de una competencia ───────────────────────────────────────── */

function GestionDeCompetencia({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const competencia = useCompetencia(id);
  const borrar = useBorrarCompetencia();
  const [pestana, setPestana] = useState('cursos');
  const [borrando, setBorrando] = useState(false);
  const c = competencia.data;

  return (
    <>
      <Drawer
        abierto={id != null}
        onCambio={(v) => { if (!v) { setPestana('cursos'); onCerrar(); } }}
        titulo={c ? c.name : 'Competencia'}
        descripcion="Qué cursos la desarrollan y quién la necesita."
        ancho="lg"
        pie={
          <div className="flex w-full flex-wrap items-center gap-2">
            <Button variant="ghost" disabled={!c} onClick={() => setBorrando(true)}>
              <i className="bi bi-trash text-danger-ink" aria-hidden="true" /> Eliminar
            </Button>
            <Button variant="ghost" className="ml-auto" onClick={onCerrar}>Cerrar</Button>
          </div>
        }
      >
        {competencia.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : competencia.isError || !c ? (
          <EstadoError err={competencia.error} que="la competencia" onReintentar={() => void competencia.refetch()} onVolver={onCerrar} />
        ) : (
          <Tabs
            valor={pestana}
            onCambio={setPestana}
            pestanas={[
              { clave: 'cursos', etiqueta: `Cursos (${c.courses.length})`, icono: 'bi-play-circle' },
              { clave: 'requisitos', etiqueta: `Requisitos (${c.requirements.length})`, icono: 'bi-briefcase' },
              { clave: 'datos', etiqueta: 'Datos', icono: 'bi-sliders' },
            ]}
          >
            <TabPanel clave="cursos"><Cursos c={c} /></TabPanel>
            <TabPanel clave="requisitos"><Requisitos c={c} /></TabPanel>
            <TabPanel clave="datos"><FormularioDeCompetencia competencia={c} /></TabPanel>
          </Tabs>
        )}
      </Drawer>

      <ConfirmDialog
        abierto={borrando}
        onCambio={(v) => { if (!v) setBorrando(false); }}
        titulo="Eliminar la competencia"
        descripcion={
          c
            ? `«${c.name}» sale de la matriz y de lo que se espera de cada cargo. Si más adelante creas una con el mismo nombre, vuelve con sus cursos y requisitos.`
            : ''
        }
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={() => {
          if (!c) return;
          borrar.mutate(c.id, {
            onSuccess: () => {
              setBorrando(false);
              onCerrar();
              Utils.toast('Competencia eliminada', 'info');
            },
            onError: (err) => {
              setBorrando(false);
              Utils.toast(err instanceof Error ? err.message : 'No se pudo eliminar', 'error');
            },
          });
        }}
      />
    </>
  );
}

function Cursos({ c }: { c: CompetenciaDetalle }) {
  const cursos = useCursos();
  const vincular = useVincularCurso();
  const desvincular = useDesvincularCurso();
  const [elegido, setElegido] = useState('');

  const vinculados = new Set(c.courses.map((x) => x.course_id));
  const disponibles = (cursos.data ?? []).filter((x) => !vinculados.has(x.id));
  const seleccion = disponibles.some((x) => String(x.id) === elegido) ? elegido : String(disponibles[0]?.id ?? '');

  return (
    <div className="flex flex-col gap-4">
      <p className="text-2 text-muted">
        Quien complete cualquiera de estos cursos adquiere la competencia, y a quien le falte se le
        recomiendan.
      </p>
      {cursos.isError ? (
        <EstadoError err={cursos.error} que="los cursos" onReintentar={() => void cursos.refetch()} />
      ) : (
        <form
          className="flex flex-wrap items-end gap-3 rounded-sm border border-line bg-sunken p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!seleccion) return;
            vincular.mutate(
              { id: c.id, course_id: Number(seleccion) },
              {
                onSuccess: () => Utils.toast('Curso vinculado', 'success'),
                onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo vincular', 'error'),
              },
            );
          }}
        >
          <Field label="Curso que la desarrolla" className="min-w-56 flex-1">
            <Select value={seleccion} onChange={(e) => setElegido(e.target.value)} disabled={cursos.isLoading || !disponibles.length}>
              {cursos.isLoading ? (
                <option>Cargando…</option>
              ) : disponibles.length ? (
                disponibles.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.title}{x.status && x.status !== 'publicado' ? ' (borrador)' : ''}
                  </option>
                ))
              ) : (
                <option>Todos los cursos ya están vinculados</option>
              )}
            </Select>
          </Field>
          <Button type="submit" size="sm" loading={vincular.isPending} disabled={!disponibles.length}>
            <i className="bi bi-link-45deg" aria-hidden="true" /> Vincular
          </Button>
        </form>
      )}

      {c.courses.length ? (
        <ul className="flex flex-col divide-y divide-line rounded-sm border border-line">
          {c.courses.map((x) => (
            <li key={x.course_id} className="flex items-center gap-2 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-3">{x.title}</span>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                loading={desvincular.isPending && desvincular.variables?.course_id === x.course_id}
                aria-label={`Desvincular ${x.title}`}
                onClick={() =>
                  desvincular.mutate(
                    { id: c.id, course_id: x.course_id },
                    { onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo desvincular', 'error') },
                  )
                }
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-3 text-muted">Ningún curso la desarrolla todavía: nadie puede adquirirla.</p>
      )}
    </div>
  );
}

function Requisitos({ c }: { c: CompetenciaDetalle }) {
  const areas = useAreasDeGestion(true);
  const cargos = useCargos(true);
  const agregar = useAgregarRequisito();
  const quitar = useQuitarRequisito();
  const [tipo, setTipo] = useState<'cargo' | 'area'>('cargo');
  const [cargo, setCargo] = useState('');
  const [area, setArea] = useState('');
  const [error, setError] = useState('');
  const idLista = useId();

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    const limpio = cargo.trim();
    const areaId = Number(area || areas.data?.[0]?.id);
    if (tipo === 'cargo' && !limpio) { setError('Escribe el cargo.'); return; }
    if (tipo === 'area' && !areaId) { setError('Elige un área.'); return; }
    setError('');
    agregar.mutate(
      { competency_id: c.id, target_position: tipo === 'cargo' ? limpio : null, area_id: tipo === 'area' ? areaId : null },
      {
        onSuccess: () => { setCargo(''); Utils.toast('Requisito agregado', 'success'); },
        onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo agregar', 'error'),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-2 text-muted">
        A quién se le pide. Sale en «Mis competencias» de cada persona de ese cargo o área, y el cargo
        cuenta en la matriz.
      </p>
      <form onSubmit={enviar} className="flex flex-col gap-3 rounded-sm border border-line bg-sunken p-3">
        {/* Radios nativos: una sola parada de Tab y las flechas del navegador. */}
        <fieldset>
          <legend className="mb-1.5 text-2 font-semibold text-ink-2">Requisito de…</legend>
          <div className="flex flex-wrap gap-2">
            {([['cargo', 'Un cargo', 'bi-briefcase'], ['area', 'Un área', 'bi-diagram-3']] as const).map(([v, t, ico]) => (
              <label
                key={v}
                className={cn(
                  'flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-2 font-semibold',
                  'pointer-coarse:min-h-11 focus-within:focus-anillo',
                  tipo === v ? 'border-brand-ink bg-brand text-on-brand' : 'border-line bg-surface text-ink-2',
                )}
              >
                <input
                  type="radio"
                  name={`${idLista}-tipo`}
                  value={v}
                  checked={tipo === v}
                  onChange={() => { setTipo(v); setError(''); }}
                  className="sr-only"
                />
                <i className={cn('bi', ico)} aria-hidden="true" /> {t}
              </label>
            ))}
          </div>
        </fieldset>

        {tipo === 'cargo' ? (
          <Field
            label="Cargo"
            error={error}
            ayuda={
              cargos.isError
                ? 'No se pudo leer el catálogo de cargos: escríbelo tal como está en los perfiles.'
                : 'Se compara sin mayúsculas ni espacios de más con el cargo de cada perfil.'
            }
          >
            <Input
              value={cargo}
              list={`${idLista}-cargos`}
              onChange={(e) => { setCargo(e.target.value); setError(''); }}
              placeholder="Ej: Asesor comercial"
            />
          </Field>
        ) : (
          <Field label="Área" error={error || (areas.isError ? 'No se pudieron cargar las áreas.' : undefined)}>
            <Select value={area} onChange={(e) => { setArea(e.target.value); setError(''); }} disabled={!areas.isSuccess}>
              {areas.isLoading && <option>Cargando…</option>}
              {(areas.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </Select>
          </Field>
        )}
        <datalist id={`${idLista}-cargos`}>
          {(cargos.data ?? []).map((x) => <option key={x.id} value={x.name} />)}
        </datalist>

        <div>
          <Button type="submit" size="sm" loading={agregar.isPending}>
            <i className="bi bi-plus-lg" aria-hidden="true" /> Agregar requisito
          </Button>
        </div>
      </form>

      {c.requirements.length ? (
        <ul className="flex flex-col divide-y divide-line rounded-sm border border-line">
          {c.requirements.map((r) => {
            const nombre = r.target_position || r.area_name || 'Requisito';
            return (
              <li key={r.id} className="flex items-center gap-2 px-3 py-2">
                <i className={cn('bi', r.target_position ? 'bi-briefcase' : 'bi-diagram-3', 'text-muted')} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-3">
                  {nombre}
                  {/* Uno viejo puede traer los dos: se enseñan los dos. */}
                  {r.target_position && r.area_name && <span className="text-muted"> · o el área {r.area_name}</span>}
                </span>
                <span className="text-1 text-muted">{r.target_position ? 'Cargo' : 'Área'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  loading={quitar.isPending && quitar.variables === r.id}
                  aria-label={`Quitar el requisito ${nombre}`}
                  onClick={() =>
                    quitar.mutate(r.id, {
                      onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo quitar', 'error'),
                    })
                  }
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-3 text-muted">Ningún cargo ni área la requiere todavía.</p>
      )}
    </div>
  );
}

/* ── Alta y datos ──────────────────────────────────────────────────────── */

/**
 * El `PUT` sustituye todos los campos, así que el área viaja SIEMPRE. Si la
 * lista de áreas no se pudo leer, el estado guarda la que ya tenía y la envía
 * tal cual —el vanilla, con un `catch` que devolvía `[]`, la borraba al
 * guardar—; y un área que ya no está activa sigue saliendo como opción.
 */
function FormularioDeCompetencia({
  competencia, onHecho,
}: {
  /** Sin competencia es un alta. */
  competencia?: CompetenciaDetalle;
  onHecho?: (id: number | null) => void;
}) {
  const edicion = !!competencia;
  const crear = useCrearCompetencia();
  const editar = useEditarCompetencia();
  const areas = useAreasDeGestion(true);
  const [nombre, setNombre] = useState(competencia?.name ?? '');
  const [descripcion, setDescripcion] = useState(competencia?.description ?? '');
  const [area, setArea] = useState(competencia?.area_id ? String(competencia.area_id) : '');
  const [nivel, setNivel] = useState(competencia?.level ?? '');
  const [error, setError] = useState('');

  const areaHuerfana =
    competencia?.area_id && areas.data && !areas.data.some((a) => a.id === competencia.area_id);

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!nombre.trim()) { setError('La competencia necesita un nombre.'); return; }
    const cuerpo: CuerpoDeCompetencia = {
      name: nombre.trim(),
      description: descripcion.trim() || null,
      area_id: area ? Number(area) : null,
      level: nivel || null,
    };
    if (edicion) {
      editar.mutate(
        { id: competencia!.id, cuerpo },
        {
          onSuccess: () => Utils.toast('Competencia actualizada', 'success'),
          onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo guardar', 'error'),
        },
      );
      return;
    }
    crear.mutate(cuerpo, {
      onSuccess: (r) => {
        Utils.toast(
          r.restaurada
            ? 'Había una competencia eliminada con ese nombre: se restauró, con sus cursos y requisitos de antes.'
            : 'Competencia creada: ahora di qué cursos la desarrollan y quién la necesita.',
          r.restaurada ? 'info' : 'success',
          r.restaurada ? 7000 : undefined,
        );
        onHecho?.(r.id);
      },
      onError: (err) => Utils.toast(err instanceof Error ? err.message : 'No se pudo crear', 'error'),
    });
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4" noValidate>
      <Field label="Nombre" error={error}>
        <Input
          value={nombre}
          maxLength={150}
          onChange={(e) => { setNombre(e.target.value); setError(''); }}
          placeholder="Ej: Atención al cliente"
        />
      </Field>
      <Field label="Descripción" opcional>
        <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Área"
          opcional
          ayuda={areas.isError ? 'No se pudieron cargar las áreas: la que tenga se queda como está.' : undefined}
        >
          <Select value={area} onChange={(e) => setArea(e.target.value)} disabled={!areas.isSuccess}>
            <option value="">{areas.isLoading ? 'Cargando…' : '— Sin área —'}</option>
            {(areas.data ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            {areaHuerfana && (
              <option value={competencia!.area_id!}>{competencia!.area_name ?? `Área ${competencia!.area_id}`} (inactiva)</option>
            )}
          </Select>
        </Field>
        <Field label="Nivel" opcional>
          <Select value={nivel} onChange={(e) => setNivel(e.target.value)}>
            <option value="">Sin especificar</option>
            {Object.entries(NIVELES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
        </Field>
      </div>
      <div className="flex justify-end gap-2">
        {!edicion && onHecho && (
          <Button type="button" variant="ghost" onClick={() => onHecho(null)}>Cancelar</Button>
        )}
        <Button type="submit" loading={crear.isPending || editar.isPending}>
          <i className="bi bi-check-lg" aria-hidden="true" /> {edicion ? 'Guardar los datos' : 'Crear competencia'}
        </Button>
      </div>
    </form>
  );
}
